import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import steamSearchService from "../../service/steamSearch.service";
import HeartButton from "../../favorites/HeartButton";
import { favoriteFromSteamGame } from "../../favorites/favoriteKey";
import RegionSelect from "../nintendoDeals/RegionSelect";
import SteamPickedGame, { PriceLine, panelClass } from "./SteamPickedGame";
import { TOPUP_REGIONS } from "./steamTopup";

const SEARCH_PLACEHOLDER = "Найти игру: «ведьмак», «elden ring», «киберпанк»…";

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Поиск игры в Steam с ценой региона и пополнением под неё прямо на странице. */
function SteamGameSearch({
  region,
  regions = [],
  onRegionChange,
  pricing,
  usdCards,
  cardsSettled,
  onRefreshCards,
  externalPick,
  initialQuery = "",
}) {
  const [text, setText] = useState(initialQuery);
  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const boxRef = useRef(null);
  const query = useDebounced(text.trim(), 300);
  const label = regions.find((item) => item.value === region)?.label;
  const hasKz = regions.some((item) => item.value === "kz");

  useEffect(() => {
    setPicked(null);
  }, [region]);

  useEffect(() => {
    if (!externalPick) return;
    setOpen(false);
    setPicked(externalPick);
    boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [externalPick]);

  useEffect(() => {
    if (query.length < 2) {
      setSearch(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    steamSearchService
      .search(region, query, controller.signal)
      .then((data) => {
        setSearch({ ...data, region });
        setOpen(true);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setSearch({ error: true, region });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [query, region]);

  useEffect(() => {
    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <section ref={boxRef} className="mx-auto mt-5 max-w-3xl px-1">
      <div className="relative">
        <div className="flex items-stretch rounded-2xl border border-gray-200 bg-white shadow-sm transition focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-100 dark:border-gray-600 dark:bg-[#2a2a2a] dark:focus-within:ring-indigo-900/50">
          {regions.length > 1 ? (
            <RegionSelect value={region} regions={regions} onChange={onRegionChange} />
          ) : null}
          <MagnifyingGlassIcon className="ml-3 h-5 w-5 shrink-0 self-center text-gray-400" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
            autoComplete="off"
            aria-label="Поиск игры в Steam"
            placeholder={SEARCH_PLACEHOLDER}
            className="min-w-0 flex-1 bg-transparent px-3 py-3.5 text-base text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
          />
          {loading ? (
            <span className="mr-4 self-center text-xs text-gray-400">ищем…</span>
          ) : null}
        </div>

        {open && search?.region === region ? (
          <div className={`${panelClass} absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden`}>
            {search.error ? (
              <p className="px-3 py-3 text-sm text-gray-500">
                Поиск временно недоступен, попробуйте позже.
              </p>
            ) : search.results?.length ? (
              search.results.map((game) => (
                <div
                  key={game.appId}
                  className="flex items-center hover:bg-indigo-50 dark:hover:bg-gray-700"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setPicked({ game, openTopup: false });
                      setOpen(false);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-3 py-2 pl-3 text-left"
                  >
                    <img
                      src={game.image || game.cover}
                      alt=""
                      loading="lazy"
                      className="h-9 w-24 shrink-0 rounded bg-gray-100 object-cover"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-900 dark:text-white">
                        {game.title}
                      </span>
                      <span className="block text-sm">
                        <PriceLine game={game} />
                      </span>
                    </span>
                  </button>
                  <HeartButton item={favoriteFromSteamGame(game, search.region)} className="mx-3" />
                </div>
              ))
            ) : (
              <p className="px-3 py-3 text-sm text-gray-500">
                Ничего не нашлось. Попробуйте другое написание или английское название.
              </p>
            )}
          </div>
        ) : null}
      </div>

      <p className="mt-2 hidden px-1 text-xs text-gray-400 sm:block">
        {region === "kz"
          ? "Выберите игру — посчитаем, сколько нужно кодов США или на сколько пополнить по логину"
          : TOPUP_REGIONS.has(region)
            ? "Выберите игру — подставим сумму пополнения Steam под её цену"
            : "Выберите игру — посчитаем, сколько нужно кодов Steam США"}
      </p>

      {picked ? (
        <SteamPickedGame
          key={`${picked.game.appId}-${region}`}
          favorite={favoriteFromSteamGame(picked.game, region)}
          game={picked.game}
          region={region}
          label={label}
          pricing={pricing}
          usdCards={usdCards}
          cardsSettled={cardsSettled}
          onRefreshCards={onRefreshCards}
          initialOpen={Boolean(picked.openTopup)}
          onTryKz={
            region === "ru" && hasKz
              ? () => {
                  setText(picked.game.title);
                  onRegionChange("kz");
                  boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              : null
          }
        />
      ) : null}
    </section>
  );
}

export default SteamGameSearch;
